import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Send, Loader2, Trash2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReportBlockControls from "@/components/ReportBlockControls";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/**
 * CommentsSheet — bottom-sheet modal that shows the comment thread on a post
 * and lets the current user reply. Loads on open, appends optimistically,
 * calls `onChange` after a successful post so the parent card can bump its
 * comment count.
 */
export default function CommentsSheet({ open, onOpenChange, post, onChange }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open || !post?.id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/posts/${post.id}/comments`);
        if (!cancelled) setRows(data || []);
      } catch {
        if (!cancelled) toast.error("Could not load comments.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, post?.id]);

  useEffect(() => {
    // Scroll to the newest comment when the list grows.
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [rows.length]);

  const submit = async () => {
    const body = text.trim();
    if (!body || posting || !user) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text: body });
      setRows((cur) => {
        const next = [...cur, data];
        onChange && onChange(next.length);
        return next;
      });
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post reply.");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/posts/${post.id}/comments/${commentId}`);
      setRows((cur) => {
        const next = cur.filter((c) => c.id !== commentId);
        onChange && onChange(next.length);
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center"
      onClick={() => onOpenChange(false)}
      data-testid="comments-sheet"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-lg bg-zinc-950 border-t sm:border border-white/10 sm:rounded-2xl h-[85vh] sm:h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-rose-500">Thread</div>
            <div className="font-display text-lg text-white">
              Replies to {post.author?.display_name}
            </div>
          </div>
          <button
            data-testid="comments-close-button"
            onClick={() => onOpenChange(false)}
            className="w-9 h-9 rounded-full text-zinc-400 hover:text-white hover:bg-white/5 flex items-center justify-center"
          >
            <X size={18} />
          </button>
        </div>

        {/* List */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-8 text-zinc-500">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading replies…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-center text-sm text-zinc-500 py-12">
              No replies yet. Be the first to say something.
            </div>
          )}
          {rows.map((c) => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <Link to={`/u/${c.author?.id}`} className="shrink-0">
                <Avatar className="w-9 h-9 border border-white/10">
                  <AvatarImage src={c.author?.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-xs">
                    {c.author?.display_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/u/${c.author?.id}`}
                    className="text-sm font-medium text-white hover:text-rose-400 truncate"
                  >
                    {c.author?.display_name || c.author?.handle}
                  </Link>
                  <span className="text-xs text-zinc-500">· {timeAgo(c.created_at)}</span>
                </div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap break-words leading-relaxed mt-0.5">
                  {c.text}
                </div>
              </div>
              {(user?.id === c.author?.id || user?.role === "admin") ? (
                <button
                  onClick={() => remove(c.id)}
                  className="shrink-0 text-zinc-500 hover:text-rose-500 self-start"
                  title="Delete"
                  data-testid={`comment-delete-${c.id}`}
                >
                  <Trash2 size={14} />
                </button>
              ) : user ? (
                <ReportBlockControls targetType="comment" targetId={c.id} authorId={c.author?.id} testid={`comment-${c.id}`} className="shrink-0 self-start" onBlocked={() => setRows((r) => r.filter((x) => x.author?.id !== c.author?.id))} />
              ) : null}
            </div>
          ))}
        </div>

        {/* Composer */}
        <div className="border-t border-white/10 p-3 pb-4">
          {user ? (
            <div className="flex items-end gap-2">
              <textarea
                data-testid="comments-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder="Reply to the thread…"
                rows={1}
                maxLength={500}
                className="flex-1 resize-none bg-black/40 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-rose-500 max-h-32"
              />
              <button
                data-testid="comments-submit"
                onClick={submit}
                disabled={!text.trim() || posting}
                className="w-11 h-11 shrink-0 rounded-full bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center"
              >
                {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-zinc-500 py-2">
              <Link to="/login" className="text-rose-400 hover:text-rose-300">Log in</Link> to reply.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
